import {
  createSignal,
  createMemo,
  splitProps,
  createEffect,
  createResource,
  Show,
  For,
} from "https://cdn.jsdelivr.net/npm/solid-js@1.9.5/+esm";
import html from "https://cdn.jsdelivr.net/npm/solid-js@1.9.5/html/+esm";
import { customShadowlessElement } from "https://cdn.jsdelivr.net/npm/@brytdesigns/web-component-utils@0.1.8/+esm";
import { useProduct } from "https://cdn.jsdelivr.net/npm/@brytdesigns/shopify-product-info-components@1.0.6/+esm";

function createWidgetSettingsUrl(storeUrl) {
  return `https://subs.api.tryprive.com/stores/${storeUrl}/settings`;
}

function createWidgetProductSellingPlansUrl({ storeUrl, productId }) {
  return `https://subs.api.tryprive.com/stores/${storeUrl}/products/${productId}/new`;
}

async function fetchWidgetSettings(storeUrl) {
  const endpoint = createWidgetSettingsUrl(storeUrl);
  const response = await fetch(endpoint, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

async function fetchProductSellingPlans({ storeUrl, productId }) {
  const endpoint = createWidgetProductSellingPlansUrl({ storeUrl, productId });
  const response = await fetch(endpoint, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

async function fetchWidgetData({ storeUrl, productId }) {
  const [settings, options] = await Promise.all([
    fetchWidgetSettings(storeUrl),
    fetchProductSellingPlans({ storeUrl, productId }),
  ]);

  return { settings, options };
}

function CustomRadio(props) {
  return html`
    <div class="relative translate-y-[.25em] h-max">
      <span
        class="border border-current h-[1em] w-[1em] rounded bg-white block transition-colors group-peer-checked:bg-current"
      ></span>
      <span
        class="block absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 w-[.5em] h-[.5em] rounded-full bg-transparent scale-75 transition-all group-peer-checked:bg-white group-peer-checked:scale-100"
      ></span>
    </div>
  `;
}

function GroupOptionLabel(props) {
  const [component, rest] = splitProps(props, ["label", "children"]);
  return html`
    <div
      class="border rounded border-section-body/40 p-2 block text-sm flex flex-col peer-checked:border-current transition-colors gap-2 group text-section-body"
    >
      <label ...${rest} class="cursor-pointer flex gap-2">
        <${CustomRadio} />
        ${() => component.label}
      </label>
      ${() => component.children}
    </div>
  `;
}

customShadowlessElement(
  "prive-widget",
  {
    storeUrl: "",
    form: "",
  },
  (props, { element }) => {
    if (!props.storeUrl)
      return console.warn("prive-widget: No store-url attributes provided");
    const [state, methods] = useProduct(element);
    const variant = () => state.product.selected_or_first_available_variant;

    const variants = createMemo(() => {
      return state.product.variants;
    });

    const [data] = createResource(
      () => ({ storeUrl: props.storeUrl, productId: state.product.id }),
      fetchWidgetData,
    );

    createEffect(() => {
      console.log("Data: ", data());
    });

    const settings = () => data()?.settings?.widget_settings;
    const sellingPlanGroups = () => data()?.options?.selling_plan_groups;
    const firstSellingPlan = () =>
      sellingPlanGroups()?.at(0)?.selling_plans?.at(0);

    const [purchaseOption, setPurchaseOption] = createSignal("sub");
    const [selectedSellingPlan, setSelectedSellingPlan] = createSignal(null);

    const formattedSellingPlanAmount = createMemo(() => {
      const sellingPlan = selectedSellingPlan();
      const discount = sellingPlan?.discount;
      const price = variant().price;
      if (!discount) return Shopify.formatMoney(price);
      let discountedPrice;
      if (sellingPlan?.discountModifier === "PERCENTAGE") {
        discountedPrice = price - price * (discount / 100);
      } else {
        discountedPrice = price - discount;
      }
      return Shopify.formatMoney(discountedPrice);
    });

    createEffect(() => {
      if (data.loading) return;
      setSelectedSellingPlan(firstSellingPlan());
    });

    const otpAllowed = () =>
      sellingPlanGroups()?.some((group) => group.type.includes("ONETIME"));

    const hasSellingPlans = () => sellingPlanGroups()?.length > 0;

    const otpIsFirst = () => settings()?.onetimeFirst;

    const hasShippingDiscount = () =>
      sellingPlanGroups()?.some((group) =>
        group.selling_plans.some((plan) => plan.shippingDiscount),
      );

    const hasDiscount = () =>
      sellingPlanGroups()?.some((group) =>
        group.selling_plans?.some((plan) => plan.discount),
      );

    function OtpOption() {
      const id = () =>
        props.form
          ? `${props.form}-purchaseOption-${state.product.id}-one`
          : `purchaseOption-${state.product.id}-one`;

      const label = () =>
        `${settings()?.onetimeCopy || "One-time purchase:"} ` +
        Shopify.formatMoney(variant().price);

      return html`
        <div>
          <input
            type="radio"
            form=${() => props.form || undefined}
            id=${id}
            name="purchase_option"
            class="sr-only peer"
            value="one"
            checked=${() => purchaseOption() === "one"}
            onChange=${() => setPurchaseOption("one")}
          />

          <${GroupOptionLabel} for=${id} label=${label}>
            <${Show} when=${() => purchaseOption() === "one"}>
              <${Show} when=${() => variants().length}>
                <select
                  ref=${(el) => {
                    setTimeout(() => {
                      el.dispatchEvent(new Event("change", { bubbles: true }));
                    });
                  }}
                  name="id"
                  form=${() => props.form}
                  class="text-sm rounded text-section-body border-current"
                  onChange=${(e) => {
                    methods.variant.update(e.target.value);
                  }}
                >
                  <${For} each=${variants()}>
                    ${(variant, i) => html`
                      <option value=${variant.id}>${variant.title}</option>
                    `}
                  <//>
                </select>
              <//>
            <//>
          <//>
        </div>
      `;
    }

    function SellingPlanOption() {
      const id = () =>
        props.form
          ? `${props.form}-purchaseOption-${state.product.id}-sub`
          : `purchaseOption-${state.product.id}-sub`;

      const defaultCopy = () =>
        hasDiscount() ? "Subscribe and Save:" : "Subscribe:";

      const label = () =>
        `${settings()?.subscribeCopy || defaultCopy()}&nbsp;${formattedSellingPlanAmount()}`;

      const Label = () => html`
        <div class="flex flex-col">
          <span innerHTML=${label} />
          <${Show} when=${() => hasShippingDiscount()}>
            <strong class="text-xs">
              Plus FREE shipping on orders over ${" "}
              ${() =>
                Shopify.formatMoney(
                  firstSellingPlan()?.shippingDiscount.requirements.amount *
                    100,
                )}
            </strong>
          <//>
        </div>
      `;

      return html`
        <div>
          <input
            type="radio"
            form=${() => props.form || undefined}
            id=${id}
            name="purchase_option"
            class="sr-only peer"
            value="sub"
            checked=${() => purchaseOption() === "sub"}
            onChange=${() => setPurchaseOption("sub")}
          />
          <${Show} when=${() => purchaseOption() === "sub"}>
            <input
              type="hidden"
              form=${() => props.form || undefined}
              id=${id}
              name="selling_plan"
              class="sr-only"
              value="${() => selectedSellingPlan()?.sellingPlanId}"
            />
          <//>
          <${GroupOptionLabel} for=${id} label=${Label}>
            <${Show} when=${() => purchaseOption() === "sub"}>
              <${Show} when=${() => variants().length}>
                <select
                  ref=${(el) => {
                    setTimeout(() => {
                      el.dispatchEvent(new Event("change", { bubbles: true }));
                    });
                  }}
                  name="id"
                  form=${() => props.form}
                  class="text-sm rounded text-section-body border-current"
                  onChange=${(e) => {
                    methods.variant.update(e.target.value);
                  }}
                >
                  <${For} each=${variants()}>
                    ${(variant, i) => html`
                      <option value=${variant.id}>${variant.title}</option>
                    `}
                  <//>
                </select>
              <//>
            <//>
          <//>
        </div>
      `;
    }

    return html`
      <${Show} when=${data}>
        <${Show} when=${hasSellingPlans}>
          <div class="flex flex-col gap-2 mb-2">
            <input
              type="hidden"
              name="selling_plan"
              form=${() => props.form}
              value=${() =>
                purchaseOption() === "sub"
                  ? selectedSellingPlan()?.sellingPlanId
                  : ""}
            />
            <${Show} when=${() => otpIsFirst() && otpAllowed()}>
              <${OtpOption} />
            <//>
            <${SellingPlanOption} />
            <${Show} when=${() => !otpIsFirst() && otpAllowed()}>
              <${OtpOption} />
            <//>
          </div>
        <//>
      <//>
    `;
  },
);
